import { IncidentReportType } from '@roadfootage/api-client'
import type {
  VendorMetadataExtractor,
  VendorEvent,
  ExtractedIncidentMeta,
  SuggestedIncidentContext
} from './types'
import { formatDateTime } from '@/utils/format'

interface TeslaEventJson {
  timestamp?: string
  city?: string
  street?: string
  est_lat?: string
  est_lon?: string
  reason?: string
  camera?: string
}

const REASON_LABELS: Record<string, string> = {
  user_interaction_dashcam_launcher_action_tapped: 'Manual Save',
  user_interaction_honk: 'Manual Save (Honk)',
  sentry_aware_object_detection: 'Sentry Mode – Object Detected',
  sentry_aware_accel_event: 'Sentry Mode – Impact Detected',
  sentry_aware_obstacle_in_path: 'Sentry Mode – Obstacle',
  footage_saved: 'Footage Saved',
  autopark_engage: 'Autopark',
  full_self_driving_takeover: 'FSD Takeover'
}

// Conservative mapping: only reason codes with a strong signal for a specific type.
// Omits manual-save and parking/FSD events where the type is ambiguous.
const TESLA_REASON_TO_INCIDENT_TYPE: Partial<Record<string, IncidentReportType>> = {
  sentry_aware_accel_event: IncidentReportType.TrafficAccident,
  sentry_aware_object_detection: IncidentReportType.BadDriving,
  sentry_aware_obstacle_in_path: IncidentReportType.BadDriving,
  user_interaction_honk: IncidentReportType.BadDriving
}

// Known Tesla USB subdirectory names and their display labels
export const TESLA_CATEGORY_LABELS: Record<string, string> = {
  SavedClips: 'Saved',
  SentryClips: 'Sentry',
  RecentClips: 'Recent',
  TrackMode: 'Track Mode'
}

function humanizeTeslaReason(reason: string | null | undefined): string {
  if (!reason) return 'Event'
  return REASON_LABELS[reason] ?? reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function parseClipStartTime(filename: string): Date | null {
  const m = filename.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})-/)
  if (!m) return null
  return new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`)
}

/** Returns the first path segment that matches a known Tesla category directory. */
function detectCategory(dirPath: string): string | null {
  for (const seg of dirPath.split('/')) {
    if (seg in TESLA_CATEGORY_LABELS) return seg
  }
  return null
}

function getEventDirPath(file: File): string {
  const rel = (file as any).webkitRelativePath as string | undefined
  if (!rel) return ''
  const parts = rel.split('/')
  parts.pop()
  return parts.join('/')
}

export interface TeslaEventInfo {
  category: string | null
  place: string
  reason: string
}

/** Typed view of the Tesla-specific fields parseEvents keeps in `rawMeta`. */
export function getTeslaEventInfo(event: VendorEvent): TeslaEventInfo {
  const raw = event.rawMeta as { place?: string; category?: string | null } | undefined
  return {
    category: raw?.category ?? null,
    place: raw?.place ?? '',
    reason: event.displayLabel
  }
}

async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

export const teslaExtractor: VendorMetadataExtractor = {
  vendorId: 'tesla',
  vendorLabel: 'Tesla USB',
  supportsDirectoryBrowse: true,

  async parseEvents(files: File[]): Promise<VendorEvent[]> {
    // Group files by their immediate parent directory path
    const byDir = new Map<string, File[]>()
    for (const file of files) {
      const dir = getEventDirPath(file)
      if (!dir) continue
      if (!byDir.has(dir)) byDir.set(dir, [])
      byDir.get(dir)!.push(file)
    }

    const events: VendorEvent[] = []

    for (const [dir, dirFiles] of byDir) {
      const category = detectCategory(dir)
      const eventJsonFile = dirFiles.find((f) => f.name === 'event.json')

      // ── event.json-based events (SavedClips, SentryClips, etc.) ──────────────
      if (eventJsonFile) {
        let meta: TeslaEventJson = {}
        try {
          const text = await readFileAsText(eventJsonFile)
          meta = JSON.parse(text)
        } catch {
          continue
        }

        const thumbnailFile = dirFiles.find((f) => f.name === 'thumb.png') ?? undefined
        const eventVideoFile = dirFiles.find((f) => f.name === 'event.mp4') ?? null
        const cameraFiles = dirFiles
          .filter((f) => f.name.endsWith('.mp4') && f.name !== 'event.mp4')
          .sort((a, b) => {
            const at = parseClipStartTime(a.name)?.getTime() ?? 0
            const bt = parseClipStartTime(b.name)?.getTime() ?? 0
            return at - bt
          })
        const filesToUpload =
          cameraFiles.length > 0 ? cameraFiles : eventVideoFile ? [eventVideoFile] : []

        const dateTime = meta.timestamp ? new Date(meta.timestamp) : undefined
        const estLat = meta.est_lat ? parseFloat(meta.est_lat) : undefined
        const estLon = meta.est_lon ? parseFloat(meta.est_lon) : undefined
        const location =
          estLat !== undefined && estLon !== undefined && !isNaN(estLat) && !isNaN(estLon)
            ? { lat: estLat, lon: estLon }
            : undefined

        const place = [meta.street, meta.city].filter(Boolean).join(', ')

        events.push({
          id: dir,
          dateTime,
          location,
          displayLabel: humanizeTeslaReason(meta.reason),
          thumbnailFile,
          filesToUpload,
          rawMeta: { place, meta, category }
        })
        continue
      }

      // ── RecentClips: flat MP4 files with no event.json ────────────────────────
      // Files are named YYYY-MM-DD_HH-MM-SS-camera.mp4 directly in the directory.
      // Group them by their timestamp prefix to form per-minute events.
      if (category === 'RecentClips') {
        const mp4Files = dirFiles.filter((f) => f.name.endsWith('.mp4'))
        if (mp4Files.length === 0) continue

        const byTimestamp = new Map<string, File[]>()
        for (const file of mp4Files) {
          const m = file.name.match(/^(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})-/)
          if (!m) continue
          const key = m[1]
          if (!byTimestamp.has(key)) byTimestamp.set(key, [])
          byTimestamp.get(key)!.push(file)
        }

        for (const [tsKey, clipFiles] of byTimestamp) {
          const sortedFiles = [...clipFiles].sort((a, b) => a.name.localeCompare(b.name))
          const dateTime =
            parseClipStartTime(sortedFiles[0].name) ?? new Date(sortedFiles[0].lastModified)

          events.push({
            id: `${dir}/${tsKey}`,
            dateTime,
            location: undefined,
            displayLabel: 'Recent Clip',
            thumbnailFile: undefined,
            filesToUpload: sortedFiles,
            rawMeta: { place: '', meta: {}, category }
          })
        }
      }
    }

    // Sort newest first
    return events.sort((a, b) => {
      const at = a.dateTime?.getTime() ?? 0
      const bt = b.dateTime?.getTime() ?? 0
      return bt - at
    })
  },

  async extractFromFiles(files: File[]): Promise<ExtractedIncidentMeta | null> {
    const first = files.find((f) => f.name.endsWith('.mp4'))
    if (!first) return null
    return { dateTime: new Date(first.lastModified) }
  },

  suggestPrimaryFile(files: File[], event: VendorEvent): File | null {
    const frontFiles = files.filter((f) => /-front\.mp4$/i.test(f.name))
    if (frontFiles.length === 0) return null
    if (!event.dateTime) return frontFiles[0]

    const eventMs = event.dateTime.getTime()
    return frontFiles.reduce((best, f) => {
      const bTime = parseClipStartTime(f.name)?.getTime() ?? eventMs
      const cTime = parseClipStartTime(best.name)?.getTime() ?? eventMs
      return Math.abs(bTime - eventMs) < Math.abs(cTime - eventMs) ? f : best
    })
  },

  suggestIncidentContext(event: VendorEvent): SuggestedIncidentContext | null {
    const meta = (event.rawMeta as any)?.meta as TeslaEventJson | undefined
    if (!meta) return null

    const incidentType = meta.reason ? TESLA_REASON_TO_INCIDENT_TYPE[meta.reason] : undefined

    const parts: string[] = [`My dashcam recorded a ${event.displayLabel} event`]
    const place = (event.rawMeta as any)?.place as string | undefined
    if (place) parts.push(`near ${place}`)
    if (event.dateTime) parts.push(`on ${formatDateTime(event.dateTime)}`)
    const description = parts.join(' ') + '.'

    return { incidentType: incidentType as string | undefined, description }
  }
}
